import { useState, useEffect, useRef, type ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Input from "@/v2-ui/input"
import { useSearchParams } from "@/v2-backoffice-client/utils"
import { useTableContext } from "./table.context"
import { GLOBAL_SEARCH_TARGET } from "./table.constants"

export type TableSearchProps = Omit<ComponentProps<"input">, "size"> & {
  /** Debounce delay in ms. Defaults to 300. */
  debounce?: number
  /**
   * Column to filter on (server-side). Sets a column filter with "contains"
   * operator. When not provided, falls back to table globalFilter (client-side).
   */
  column?: string
  /**
   * Query-string key the search text is read from on mount and written to
   * as it changes (replace, never a history entry; removed when empty), so
   * a board can be deep-linked with a search already applied. Off by
   * default: most searches are a moment's filter, not a place.
   */
  urlParam?: string
}

function TableSearch(props: TableSearchProps) {
  const {
    placeholder = "Search...",
    className,
    debounce = 300,
    column,
    urlParam,
    ...inputProps
  } = props

  const { table, registerSearch } = useTableContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const [value, setValue] = useState(
    () => (urlParam ? searchParams.get(urlParam) : null) ?? "",
  )

  // Tell the table which filter this box writes, so it can tell a search
  // from any other filter: the status pills pause while one is active.
  useEffect(() => {
    registerSearch(column ?? GLOBAL_SEARCH_TARGET)
    return () => {
      registerSearch(null)
    }
  }, [registerSearch, column])

  // The URL pair changes identity on EVERY navigation (a row's panel
  // opening included), and the apply effect must only run when the typed
  // value changes: re-applying an unchanged filter hands TanStack a new
  // filters array, which every "did the filters change" consumer read as a
  // change. Read the latest pair through a ref instead of depending on it.
  const searchParamsRef = useRef({ searchParams, setSearchParams })
  searchParamsRef.current = { searchParams, setSearchParams }

  // The value the table holds for this box, against what the box last
  // wrote. When they differ, something else changed it (a filter chip's ×,
  // Clear all, a reset): the box follows, so it never shows text the table
  // is no longer searching for — and the pills, which resume the moment the
  // search filter is gone, are never contradicted by a stale box.
  const appliedValue = column
    ? table.getColumn(column)?.getFilterValue()
    : table.getState().globalFilter
  const lastAppliedRef = useRef<unknown>(undefined)
  useEffect(() => {
    if (appliedValue === lastAppliedRef.current) return
    lastAppliedRef.current = appliedValue
    setValue(typeof appliedValue === "string" ? appliedValue : "")
  }, [appliedValue])

  useEffect(() => {
    const t = setTimeout(() => {
      const next = value || undefined
      lastAppliedRef.current = next
      if (column) {
        const col = table.getColumn(column)
        if (col && col.getFilterValue() !== next) col.setFilterValue(next)
      } else if (table.getState().globalFilter !== next) {
        table.setGlobalFilter(next)
      }
      const { searchParams, setSearchParams } = searchParamsRef.current
      if (urlParam && (searchParams.get(urlParam) ?? "") !== value) {
        // The wrapper merges with the other params and drops the key on "".
        setSearchParams({ [urlParam]: value }, { replace: true })
      }
    }, debounce)
    return () => {
      clearTimeout(t)
    }
  }, [value, debounce, table, column, urlParam])

  return (
    <div className={cn("ui-table-search-wrapper", className)}>
      <Icon src="search" className="ui-table-search-icon" size={12} />
      <Input
        className="ui-table-search"
        type="search"
        size="xs"
        autoFocus
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          setValue(e.target.value)
        }}
        {...inputProps}
      />
    </div>
  )
}

export default TableSearch
